import React from 'react';
import { Checkbox, FormControlLabel, Radio } from '@mui/material';
import { Controller } from 'react-hook-form';
import { attributeTypes, Images, sanitizeHtml } from 'utils';
import { resolveRichTextImages } from './resolveRichTextImages';
import { hasRichTextContent, readOnlyBoxImageUrl, readOnlyBoxText } from './fillableFields';

const OPTION_LIST_TYPES = [attributeTypes.CHECK_BOX, attributeTypes.RADIO_BUTTON];

/** The default the replaced Controller registered: checkboxes an array, radios a value or null. */
const registeredDefault = (item) => {
    if (item?.type === attributeTypes.CHECK_BOX) return Array.isArray(item?.value) ? item.value : [];
    if (item?.type === attributeTypes.RADIO_BUTTON) return item?.value || null;
    return item?.value;
};

/**
 * A locked field has no input, but its value must still be saved. The control it replaces
 * registered `name` with the template value as default; goal-block paths
 * (`sections.X.goals.<id>.attributes.i.value`) get their value only that way, so without it a
 * preset read-only value shows on screen but is missing from the saved note. No rules: staff
 * can't change it, so it isn't validated.
 */
const KeepValueRegistered = ({ control, name, item }) =>
    control ? <Controller name={name} control={control} defaultValue={registeredDefault(item)} render={() => null} /> : null;

/**
 * A read-only field while filling a note (Figma "Read-only / Auto-filled" and
 * "Read-only / Empty"): the label, then the value in a locked grey box with a
 * lock icon, "Not available" when empty. Rich Text keeps its html, checkboxes
 * and radios their options (disabled), a signature or an image upload is drawn
 * as its image, any other value as one line of text. Used by `ReportBuilder` and `FormBuilder`'s classic layout for every
 * type `isReadOnlyBoxType` accepts. Pass the form's `control` so the value stays registered and saved.
 * `onUnlockClick` (progress report builder only): the box becomes clickable and selects the field, so
 * Properties can turn its Read Only off for that report.
 */
export const LockedField = ({ item, value, name, label, required, imageUrls, style, control, onUnlockClick }) => {
    const rootProps = onUnlockClick
        ? {
              className: 'report-builder-readonly-field report-builder-locked-unlockable',
              onClick: onUnlockClick,
              title: 'Read only. Click, then turn off Read Only in Properties to edit it on this report.',
          }
        : { className: 'report-builder-readonly-field' };
    // Checkboxes and radios keep their option list, disabled, inside the box.
    if (OPTION_LIST_TYPES.includes(item?.type) && item?.properties?.options?.length) {
        const picked = (Array.isArray(value) ? value : value ? [value] : []).map((opt) => opt?.id);
        const Control = item.type === attributeTypes.RADIO_BUTTON ? Radio : Checkbox;
        return (
            <div name={name} {...rootProps} style={style}>
                <KeepValueRegistered control={control} name={name} item={item} />
                {label && <p className="preview-creator-info-line-title">{`${label}${required ? '*' : ''}`}</p>}
                <div
                    className={`report-builder-locked-box report-builder-locked-box-rich${
                        picked.length ? '' : ' report-builder-locked-box-empty'
                    }`}
                >
                    <div className="report-builder-locked-box-text report-builder-locked-box-options">
                        {item.properties.options.map((option) => (
                            <FormControlLabel
                                key={option?.id}
                                className="report-radio-label"
                                disabled
                                control={<Control checked={picked.includes(option?.id)} size="small" style={{ padding: '2px 8px' }} />}
                                label={option?.name}
                            />
                        ))}
                    </div>
                    <img src={Images.lockGray} alt="" className="report-builder-locked-box-icon" />
                </div>
            </div>
        );
    }

    const html =
        item?.type === attributeTypes.RICH_TEXT && hasRichTextContent(value) ? sanitizeHtml(resolveRichTextImages(value, imageUrls)) : '';
    const imageUrl = readOnlyBoxImageUrl(item, value);
    const text = html || imageUrl ? '' : readOnlyBoxText(item, value);
    const isEmpty = !html && !imageUrl && !text;
    const isTall = !!html || !!imageUrl;

    return (
        <div name={name} {...rootProps} style={style}>
            <KeepValueRegistered control={control} name={name} item={item} />
            {label && <p className="preview-creator-info-line-title">{`${label}${required ? '*' : ''}`}</p>}
            <div
                className={`report-builder-locked-box${isEmpty ? ' report-builder-locked-box-empty' : ''}${
                    isTall ? ' report-builder-locked-box-rich' : ''
                }`}
            >
                {html ? (
                    <div className="report-builder-locked-box-text richtext-readonly-content" dangerouslySetInnerHTML={{ __html: html }} />
                ) : imageUrl ? (
                    <div className="report-builder-locked-box-text">
                        <img src={imageUrl} alt={label || ''} className="report-builder-locked-box-image" />
                    </div>
                ) : (
                    <span className="report-builder-locked-box-text">{text || 'Not available'}</span>
                )}
                <img src={Images.lockGray} alt="" className="report-builder-locked-box-icon" />
            </div>
        </div>
    );
};
